// src/OrthoLayer.jsx
import React, { useEffect, useState, useRef } from "react";
import { TileLayer, useMap } from "react-leaflet";

// --- Helpers: Date Formatting ---
const toDateKey = (raw) => {
  if (raw == null) return null;
  if (typeof raw === "string" && /^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const d = new Date(raw);
  if (!isNaN(d.getTime())) return d.toISOString().split("T")[0];
  return null;
};

// --- Helpers: Coordinate Conversion (EPSG:3857 -> EPSG:4326) ---
const mercatorToLatLng = (x, y) => {
  const rMajor = 6378137; // Earth Radius
  const shift = Math.PI * rMajor;
  const lon = (x / shift) * 180.0;
  let lat = (y / shift) * 180.0;
  lat = (180 / Math.PI) * (2 * Math.atan(Math.exp((lat * Math.PI) / 180.0)) - Math.PI / 2.0);
  return [lat, lon];
};

export default function OrthoLayer({ latestSurvey, pane, apiBase }) {
  const map = useMap();
  
  // --- State ---
  const [layers, setLayers] = useState([]);
  
  // --- Refs ---
  const isCancelledRef = useRef(false);
  const hasZoomedRef = useRef(false);

  // --- Logic: Build URL ---
  const buildTileUrl = (cand, apiBase) => {
    // 1. We MUST use the 'tile_url_root' from the server because it contains
    // the correct SLUGS (e.g., 'site-a' instead of 'Site A').
    if (cand.tile_url_root) {
      try {
        // Parse the server-provided URL to get the path (e.g. /ortho/site-a/2023-01-01/layer/tiles/)
        const serverUrl = new URL(cand.tile_url_root);
        const serverPath = serverUrl.pathname; 

        // Strip trailing slash from apiBase
        const cleanBase = apiBase.replace(/\/+$/, "");
        
        // Construct: apiBase + path from server + tile pattern
        return `${cleanBase}${serverPath}{z}/{x}/{y}.png`;
      } catch (e) {
        console.warn("[OrthoLayer] Could not parse tile_url_root:", cand.tile_url_root);
      }
    }

    // Fallback: If no tile_url_root, we skip (manual construction is too risky for slugs)
    return null;
  };

  // --- Logic: Fetch & Parse XML for Bounds ---
  const fetchLayerMetadata = async (tileUrlTemplate) => {
    if (!tileUrlTemplate) return null;
    try {
      // Replace tile pattern with metadata file
      const xmlUrl = tileUrlTemplate.replace(/\{z\}\/\{x\}\/\{y\}\.png$/, "tilemapresource.xml");
      
      const resp = await fetch(xmlUrl);
      if (!resp.ok) return null;
      
      const text = await resp.text();
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(text, "text/xml");
      
      const bbox = xmlDoc.querySelector("BoundingBox");

      if (bbox) {
        const minx = parseFloat(bbox.getAttribute("minx"));
        const miny = parseFloat(bbox.getAttribute("miny"));
        const maxx = parseFloat(bbox.getAttribute("maxx"));
        const maxy = parseFloat(bbox.getAttribute("maxy"));

        let sw, ne;
        // Check Coordinate System (Mercator vs LatLng)
        if (Math.abs(minx) <= 180 && Math.abs(miny) <= 90) {
            sw = [miny, minx]; 
            ne = [maxy, maxx];
        } else {
            sw = mercatorToLatLng(minx, miny);
            ne = mercatorToLatLng(maxx, maxy);
        }
        return [sw, ne]; 
      }
    } catch (e) {
      console.warn("[OrthoLayer] Error parsing metadata:", e);
    }
    return null;
  };

  // --- Logic: Main Discovery ---
  const discoverOrthoLayers = async () => {
    if (!latestSurvey?.location_name || !latestSurvey?.survey_time) {
      setLayers([]);
      return;
    }

    try {
      const params = new URLSearchParams({
        location_name: String(latestSurvey.location_name),
        survey_time: toDateKey(latestSurvey.survey_time),
      });
      
      // MATCH BACKEND: Use /layers/tiles-discovery (not /ortho/)
      const url = `${apiBase}/layers/tiles-discovery?${params.toString()}`;
      
      console.log(`[OrthoLayer] Discovering: ${url}`);
      const r = await fetch(url);
      
      if (!r.ok) throw new Error("Failed to fetch tiles");
      
      const json = await r.json();
      const foundCandidates = Array.isArray(json.found) ? json.found : [];
      
      if (isCancelledRef.current) return;

      const processedLayers = await Promise.all(foundCandidates.map(async (cand, index) => {
        const layerName = cand.layer_slug || cand.layer_name || `Ortho ${index + 1}`;
        
        // FIX: Add index to ID to guarantee uniqueness for multiple layers
        const uniqueId = `auto-ortho-${layerName}-${index}`; 
        
        // Pass apiBase to builder
        const tileUrl = buildTileUrl(cand, apiBase);
        
        // Only proceed if we successfully built a URL
        if (!tileUrl) return null;

        const bounds = await fetchLayerMetadata(tileUrl);

        return {
          id: uniqueId,
          name: layerName,
          url: tileUrl,
          bounds: bounds,
          opacity: 1.0, 
          visible: true,
          // FIX: Assign Z-Index so layers stack correctly (400 is standard overlay base)
          zIndex: 400 + index
        };
      }));

      // Filter out nulls (failed URL builds)
      const validLayers = processedLayers.filter(l => l !== null);

      setLayers(validLayers);
      hasZoomedRef.current = false; 

      window.dispatchEvent(new CustomEvent("layers-discovered", {
         detail: { 
           location: latestSurvey.location_name, 
           survey_time: latestSurvey.survey_time, 
           layer_names: validLayers.map(l => l.name),
           candidates: foundCandidates 
         }
      }));

    } catch (err) {
      console.error("[OrthoLayer] Discovery error:", err);
      if (!isCancelledRef.current) setLayers([]);
    }
  };

  // --- Effects ---

  // 1. Trigger discovery
  useEffect(() => {
    isCancelledRef.current = false;
    discoverOrthoLayers();
    return () => { isCancelledRef.current = true; };
  }, [latestSurvey, apiBase]);

  // 2. Auto Zoom
  useEffect(() => {
    if (layers.length > 0 && !hasZoomedRef.current) {
      const layerWithBounds = layers.find(l => l.bounds && l.bounds.length === 2);
      if (layerWithBounds) {
        try {
            console.log(`[OrthoLayer] Zooming to ${layerWithBounds.name}`, layerWithBounds.bounds);
            map.fitBounds(layerWithBounds.bounds, { padding: [20, 20], maxZoom: 19 });
            hasZoomedRef.current = true;
        } catch(e) { console.warn("Zoom failed:", e); }
      }
    }
  }, [layers, map]);

  // 3. Listen for Sidebar Events
  useEffect(() => {
    const handleToggle = (ev) => {
      const { id, visible } = ev?.detail || {};
      setLayers(prev => prev.map(l => (l.id === id || l.name === id) ? { ...l, visible: !!visible } : l));
    };

    const handleOpacity = (ev) => {
      const { id, opacity: val } = ev?.detail || {};
      setLayers(prev => prev.map(l => (l.id === id || l.name === id) ? { ...l, opacity: Number(val) } : l));
    };

    const handleDataUpdate = (ev) => {
       const detail = ev.detail || {};
       if (latestSurvey && 
           detail.location_name === latestSurvey.location_name && 
           toDateKey(detail.survey_time) === toDateKey(latestSurvey.survey_time)) {
           discoverOrthoLayers();
       }
    };

    window.addEventListener("map-toggle-layer", handleToggle);
    window.addEventListener("map-set-opacity", handleOpacity);
    window.addEventListener("survey-data-updated", handleDataUpdate);

    return () => {
      window.removeEventListener("map-toggle-layer", handleToggle);
      window.removeEventListener("map-set-opacity", handleOpacity);
      window.removeEventListener("survey-data-updated", handleDataUpdate);
    };
  }, [latestSurvey]);

  // --- Render ---
  return (
    <>
      {layers.map(layer => (
        layer.visible && (
          <TileLayer
            key={layer.id}
            url={layer.url}
            tms={true}
            maxZoom={24}
            minZoom={0}
            opacity={layer.opacity}
            pane={pane}
            // FIX: Pass zIndex to React-Leaflet to handle visual stacking
            zIndex={layer.zIndex}
            eventHandlers={{
              tileerror: (e) => { 
                console.warn("Tile Error:", e.coords, e.target._url);
                e.tile.style.display = 'none'; 
              }
            }}
          />
        )
      ))}
    </>
  );
}
